import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/bessarabca";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const bessarabca: CaseStudy = {
  slug: "bessarabca",
  title: "Бессарабка",
  cardTitle: "Поддержка и развитие интернет-магазина фермерских продуктов «Бессарабка»",
  subtitle: "Фермерский базар с доставкой продуктов",
  industry: { label: "Еда и продукты", slug: "food" },
  eyebrow: "Интернет-магазин · Фермерские продукты",
  h1: "Поддержка и развитие интернет-магазина «Бессарабка»",
  lead:
    "Развиваем сайт фермерского базара «Бессарабка»: большой каталог фермерских продуктов и блюд ресторанов, оформление заказа с персональным сервис-ассистентом, доставку и подарочные сертификаты.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить развитие интернет-магазина" },

  seo: {
    primaryKeyword: "поддержка и развитие интернет-магазина продуктов",
    secondaryKeywords: [
      "развитие интернет-магазина продуктов",
      "поддержка сайта доставки продуктов",
      "e-commerce для фермерского рынка",
      "сайт доставки фермерских продуктов",
    ],
    semanticEntities: [
      "фермерский каталог",
      "продуктовый интернет-магазин",
      "персональный сервис-ассистент",
      "доставка продуктов",
      "подарочный сертификат",
      "сезонные предложения",
      "корзина",
    ],
    excludedKeywords: [
      "доставка продуктов",
      "фермерские продукты купить",
      "мясо доставка",
      "сыр купить",
    ],
    title: "Развитие сайта «Бессарабка» — кейс HAY.agency",
    description:
      "Кейс HAY.agency: развитие сайта фермерского базара с большим каталогом, доставкой, персональным сервис-ассистентом и подарочными сертификатами.",
    h1: "Поддержка и развитие интернет-магазина «Бессарабка»",
    slug: "bessarabca",
    canonicalUrl: `${SITE_URL}/cases/bessarabca`,
    ogTitle: "Поддержка и развитие интернет-магазина «Бессарабка»",
    ogDescription:
      "Каталог фермерских продуктов, персональный сервис-ассистент, доставка, корзина и подарочные сертификаты.",
    ogImage: `${SITE_URL}${dir}/bessarabca-og.webp`,
    robotsIndex: true,
    datePublished: "2023-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, корзина и сценарии доставки",
    },
    relatedIndustry: {
      label: "Продвижение интернет-магазинов",
      to: routes.seo,
      note: "Видимость каталога продуктов",
    },
    relatedCases: [
      {
        label: "Разработка интернет-магазина цветов «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Каталог, карточка товара и доставка",
      },
      {
        label: "Интернет-магазин профессиональной косметики Quantum Shop",
        to: "/cases/quantum-shop",
        note: "Мультибрендовый каталог и корзина",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужна поддержка интернет-магазина продуктов?",
    text: "Возьмём на себя развитие каталога, доставки и сервисных сценариев.",
    button: "Обсудить развитие интернет-магазина",
  },

  client: {
    name: "Бессарабка",
    url: "https://bessarabca.shop/",
    city: "Москва",
    industry: "Продукты питания и доставка",
  },
  year: "С 2023 года",
  services: [
    "Поддержка интернет-магазина",
    "Развитие каталога продуктов",
    "Оформление заказа и доставки",
    "Сервис-ассистент и сертификаты",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Поддержка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "Бессарабка" },
    { label: "Отрасль", value: "Продукты питания и доставка" },
    { label: "Тип проекта", value: "Интернет-магазин фермерских продуктов" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог фермерских продуктов и блюд ресторанов с поиском по категориям",
      "Оформление заказа на сайте, в чате или по телефону",
      "Привязка персонального сервис-ассистента к заказу с учётом упаковки и подготовки продуктов",
      "Оформление доставки и корзина",
      "Подарочный сертификат и сезонные предложения",
      "Постоянное развитие и поддержка сайта",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "«Бессарабка» — фермерский базар с доставкой, где каталог объединяет продукты фермерских хозяйств и готовые блюда ресторанов. Такому ассортименту нужен сайт, который выдерживает разнородный каталог и остаётся простым в оформлении заказа.",
        "HAY.agency поддерживает и развивает сайт: в футере live-сайта указано «Поддержка веб-сайта — hay.agency».",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: тёплая продуктовая подача",
    paragraphs: [
      "Интерфейс собран на тёплой beige/cream палитре — витрина читается как фермерский рынок, а не типовой продуктовый агрегатор.",
      "Каталог и карточки товара показывают реальные продукты и корзину, оформление заказа доступно с любой страницы.",
    ],
    bullets: [
      "Тёплая beige/cream палитра вместо холодного каталога",
      "Каталог фермерских продуктов и блюд ресторанов в одной структуре",
      "Оформление заказа на сайте, в чате или по телефону",
    ],
  },

  identity: {
    fonts: [
      { name: "Inter", role: "Заголовки", sample: "Каталог продуктов" },
      { name: "Inter", role: "Интерфейс и описания", sample: "Оформление доставки" },
    ],
    colors: [
      { hex: "#F6F0E4", name: "Фон витрины" },
      { hex: "#8C7A4E", name: "Акцент бренда" },
      { hex: "#2A2620", name: "Текст" },
    ],
  },

  cover: img(
    "bessarabca-case-cover",
    "Главная страница интернет-магазина фермерских продуктов «Бессарабка»",
    6,
  ),

  gallery: [
    img("bessarabca-catalog", "Каталог фермерских продуктов и блюд ресторанов «Бессарабка»", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: каталог, оформление заказа и доставка в один сценарий.",
      "Состояния корзины и доставки в мобильной версии показаны заполненными, как в реальном использовании.",
    ],
    images: [
      mobile("bessarabca-mobile-home", "Мобильная главная интернет-магазина «Бессарабка»"),
      mobile("bessarabca-mobile-cart", "Мобильная корзина и доставка «Бессарабка»"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Продуктовый каталог",
      paragraphs: [
        "Каталог объединяет категории, поиск и карточки товаров: фермерские продукты и блюда ресторанов представлены в единой структуре.",
      ],
      bullets: ["Категории и поиск по каталогу", "Карточки товаров", "Сезонные предложения"],
    },
    {
      id: "service",
      title: "Персональный сервис",
      paragraphs: [
        "К заказу привязывается персональный сервис-ассистент, который учитывает комментарии к продуктам, упаковку и подготовку.",
      ],
      bullets: ["Комментарии к продуктам и упаковке", "Роль сервис-ассистента в заказе"],
    },
    {
      id: "delivery",
      title: "Доставка и сертификаты",
      paragraphs: [
        "Оформление доставки, корзина и подарочный сертификат работают как чистые, готовые к покупке сценарии.",
      ],
      bullets: ["Оформление доставки", "Корзина", "Подарочный сертификат"],
    },
    {
      id: "support",
      title: "Поддержка сайта",
      paragraphs: [
        "HAY.agency регулярно развивает сайт: обновляет каталог, сценарии заказа и сервисные разделы.",
      ],
      bullets: ["Регулярное развитие каталога", "Поддержка сценариев заказа и доставки"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Поддержка интернет-магазина", to: routes.ecom },
      { label: "Развитие каталога и доставки", to: routes.ecom },
    ],
  },
};
